import React, {useMemo} from 'react';
import {useNavigate} from 'react-router-dom';
import {type Chapter, type Course} from '../../../api/courcesService';
import {getProgressInfo} from "../../../utils/getProgressInfo";

interface CourseChapterProps {
  chapter: Chapter;
  chapterIdx: number;
  course: Course;
  courseWithProgress: Course;
  expanded: boolean;
  onToggle: () => void;
}

const CourseChapter: React.FC<CourseChapterProps> = ({
                                                       chapter,
                                                       chapterIdx,
                                                       course,
                                                       courseWithProgress,
                                                       expanded,
                                                       onToggle,
                                                     }) => {
  const navigate = useNavigate();

  const progressInfo = useMemo(() => getProgressInfo(course, courseWithProgress), [course, courseWithProgress]);

  return (
    <div className="course-detail-chapter">
      <div
        className="course-detail-level-header"
        onClick={onToggle}
        style={{cursor: 'pointer'}}
      >
        <div className="course-detail-level-title">
          Level {chapterIdx + 1}: {chapter.title}
        </div>
        <div className="course-detail-level-chevron">
          <img
            src={expanded ? "/images/chevron-up.png" : "/images/chevron-down.png"}
            alt={expanded ? "Collapse" : "Expand"}
          />
        </div>
      </div>

      {expanded && (
        <div className="course-detail-levels-list">
          {progressInfo && progressInfo.length > 0 ? (
            progressInfo[chapterIdx].chapterInfo.map((progress) => {
              const isFinished = progress.isFinished;
              const isCurrentLesson = progress.isCurrentLesson;
              const isFirstAvailable = progress.isFirstAvailable;
              const isNextAvailable = progress.isNextAvailable;

              // Determine which icon to show
              let lessonIcon = progress.lessonIcon;
              let iconAlt = progress.iconAlt;

              const isClickable = isFinished || isCurrentLesson || isFirstAvailable || isNextAvailable;

              return (
                <div
                  key={progress.lesson.id}
                  className={`course-detail-level-item ${isFinished ? 'completed' : ''} ${isCurrentLesson || isFirstAvailable || isNextAvailable ? 'active' : ''} ${!isClickable ? 'locked' : ''}`}
                  onClick={() => {
                    if (isFinished) {
                      // For finished lessons, navigate to learn page
                      navigate(`/course/${course.id}/lesson/${progress.lesson.id}/learn`);
                    } else if (isCurrentLesson) {
                      // For current lesson, navigate directly to learn page with appropriate fragment
                      const fragmentIndex = courseWithProgress?.progressInfo?.fragmentIndex || 0;
                      navigate(`/course/${course.id}/lesson/${progress.lesson.id}/learn?fragment=${fragmentIndex}`);
                    } else if (isFirstAvailable || isNextAvailable) {
                      // For available lessons, navigate to start page
                      navigate(`/course/${course.id}/lesson/${progress.lesson.id}/start`);
                    }
                  }}
                  style={{
                    cursor: isClickable ? 'pointer' : 'not-allowed',
                    pointerEvents: isClickable ? 'auto' : 'none'
                  }}
                >
                  <div className="course-detail-lesson-content">
                    {progress.lesson.title}
                  </div>
                  <div className="course-detail-lesson-icon">
                    <img src={lessonIcon} alt={iconAlt}/>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="course-detail-level-item locked">No lessons in this level.</div>
          )}
        </div>
      )}
    </div>
  );
};

export default CourseChapter;
